/**
 * useOutsideClick Hook
 * Detects clicks outside a referenced element
 */

import { useEffect, useRef } from 'react';

/**
 * useOutsideClick Hook
 * Calls handler when clicking outside the ref'd element
 * 
 * @param {object} options - Configuration options
 * @param {React.RefObject} options.ref - Reference to the element
 * @param {function} options.handler - Callback when clicking outside
 * @param {boolean} options.enabled - Whether the listener is active
 * 
 * @example
 * const ref = useRef();
 * useOutsideClick({
 *   ref,
 *   handler: () => setIsOpen(false),
 * });
 */
export const useOutsideClick = ({
  ref,
  handler,
  enabled = true,
}) => {
  const savedHandler = useRef(handler);

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;

    const listener = (event) => {
      const el = ref?.current;
      
      // Do nothing if clicking ref's element or descendent elements
      if (!el || el.contains(event.target)) {
        return;
      }

      savedHandler.current(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, enabled]);
};

export default useOutsideClick;
